import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { acceptShareLink } from '../../services/sharing';
import { Loader2, Link as LinkIcon } from 'lucide-react';

export function ShareAcceptPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const { currentUser, loading, signInAnonymously } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const anonymousSignInAttempted = useRef(false);

  useEffect(() => {
    if (!shareId) return;
    if (loading) return;

    if (!currentUser) {
      // Sign in anonymously for view-only access instead of redirecting to /auth
      if (!anonymousSignInAttempted.current) {
        anonymousSignInAttempted.current = true;
        signInAnonymously().catch((err) => {
          setError('Failed to set up anonymous access. Please try signing in.');
          console.error('Anonymous sign-in failed:', err);
        });
      }
      return;
    }

    // Accept the share link (works for both anonymous and authenticated users)
    acceptShareLink(shareId, currentUser.uid)
      .then(({ projectId }) => {
        navigate(`/project/${projectId}`, { replace: true });
      })
      .catch((err) => {
        setError(err.message || 'Failed to accept share link');
      });
  }, [shareId, currentUser, loading, navigate, signInAnonymously]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white border border-gray-200 rounded-lg p-6 w-full max-w-md text-center">
        <LinkIcon className="h-8 w-8 text-primary mx-auto mb-3" />
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Joining shared project</h1>
        {error ? (
          <div>
            <p className="text-sm text-red-600 mb-3">{error}</p>
            <a href="/auth" className="text-sm text-primary hover:underline">
              Sign in to continue
            </a>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Setting things up...</span>
          </div>
        )}
      </div>
    </div>
  );
}
